import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../components/CustomButton';
import EditableSection from '../../components/EditableSection';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import { useUpdatePrivacyOfficerMutation } from '../../api/account/accountApiSlice';
import { TenantPrivacyOfficer } from '../../api/account/accountApiTypes';
import { enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';
import { shownLegalTexts } from './legalTextsVisibility';

type Field = keyof TenantPrivacyOfficer;

const FIELDS: { key: Field, labelKey: string, type?: string }[] = [
    { key: 'company', labelKey: 'account.privacy_officer.company' },
    { key: 'name', labelKey: 'account.privacy_officer.name' },
    { key: 'street', labelKey: 'account.privacy_officer.street' },
    { key: 'postalCode', labelKey: 'account.privacy_officer.postal_code' },
    { key: 'city', labelKey: 'account.privacy_officer.city' },
    { key: 'email', labelKey: 'account.privacy_officer.email', type: 'email' },
    { key: 'phone', labelKey: 'account.privacy_officer.phone', type: 'tel' },
];

const asForm = (officer: TenantPrivacyOfficer | null): Record<Field, string> => ({
    company: officer?.company ?? '',
    name: officer?.name ?? '',
    street: officer?.street ?? '',
    postalCode: officer?.postalCode ?? '',
    city: officer?.city ?? '',
    email: officer?.email ?? '',
    phone: officer?.phone ?? '',
});

type PrivacyOfficerSectionProps = {
    readOnly: boolean;
    /** The officer of the tenant IN VIEW — passed in, never re-queried (#1334). */
    privacyOfficer: TenantPrivacyOfficer | null;
    /** The tenant's own name, shown as what applies while no officer is set,
     *  and proposed as the firm when Bearbeiten opens without one. */
    organizationName: string;
};

/**
 * The data protection officer named on the booking page's privacy policy — an
 * external firm or a named person. Optional: without one, the privacy policy
 * names the tenant itself with its contact email, which is what every tenant
 * showed before this existed.
 *
 * In Account, beside the Impressum and the legal texts: it is part of the
 * tenant's legal identity (ADR-0005).
 */
const PrivacyOfficerSection = ({ readOnly, privacyOfficer, organizationName }: PrivacyOfficerSectionProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [updatePrivacyOfficer, { isLoading: saving }] = useUpdatePrivacyOfficerMutation();
    // One editable unit (design-decisions › Bearbeiten und Speichern). The view
    // is derived from the stored officer on every render; the Account page
    // remounts this card per tenant, so a draft never crosses a switch.
    const draft = useEditableDraft<Record<Field, string>>(asForm(privacyOfficer));
    const form = draft.value;
    const [error, setError] = useState<string | null>(null);

    /** Closes the unit only when the save went through — a failed reset must
     *  leave the draft open with the officer still stored. */
    const submit = async (body: Record<Field, string> | null) => {
        setError(null);
        try {
            await updatePrivacyOfficer(body).unwrap();
            dispatch(enqueueSuccessSnackbar({ message: t('account.saved'), autoHideDuration: 3000 }));
            draft.finish(body ?? asForm(null));
        } catch (err) {
            setError(errorMessageFrom(err, t('account.privacy_officer.save_failed'), t));
        }
    };

    /** Bearbeiten. An officer is most often the tenant's own company, so an
     *  empty firm is proposed as the organization — visibly a change, to be
     *  confirmed with Speichern or overwritten for an external provider. */
    const startEdit = () => {
        setError(null);
        draft.startEdit();
        if (!form.company.trim() && organizationName) {
            draft.setDraft({ ...form, company: organizationName });
        }
    };

    /** The fields — locked in the view, editable in the draft. */
    const renderFields = (locked: boolean) => (
        <div className={'flex flex-col gap-4'}>
            {!privacyOfficer && (
                <p className={'text-sm text-foreground'}>
                    {t('account.privacy_officer.default_applies', { organization: organizationName })}
                </p>
            )}
            <div className={'grid grid-cols-1 sm:grid-cols-2 gap-3'}>
                {FIELDS.map(({ key, labelKey, type }) => (
                    <div key={key} className={'flex flex-col'}>
                        <label className={'text-xs text-foreground/60 mb-1'} htmlFor={`privacy-officer-${key}`}>
                            {t(labelKey)}
                        </label>
                        <input
                            id={`privacy-officer-${key}`}
                            type={type ?? 'text'}
                            value={form[key]}
                            disabled={locked || saving}
                            maxLength={200}
                            onChange={(event) => draft.setDraft({ ...form, [key]: event.target.value })}
                            className={'rounded-[5px] border border-foreground/15 px-3 py-2 text-sm text-foreground'
                                + ' bg-transparent disabled:bg-foreground/5'}
                        />
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <EditableSection
            id={'privacy-officer'}
            title={t('account.privacy_officer.title')}
            description={t('account.privacy_officer.intro')}
            readOnly={readOnly}
            editing={draft.editing}
            isDirty={draft.isDirty}
            saving={saving}
            canSave={!!form.company.trim() && !!form.email.trim()}
            error={error}
            onEdit={startEdit}
            onCancel={() => { setError(null); draft.cancel(); }}
            onSave={() => { void submit(form); }}
            view={renderFields(true)}
        >
            {renderFields(false)}
            {/* Removing the officer is a write of its own (null), not a draft
                state: an empty officer cannot be saved. Offered while editing,
                because nothing changes before "Bearbeiten". */}
            {privacyOfficer && (
                <div>
                    <CustomButton
                        title={t('account.privacy_officer.reset')}
                        onClick={() => { void submit(null); }}
                        isFilled={false}
                        disabled={saving}
                    />
                </div>
            )}
        </EditableSection>
    );
};

type AccountPrivacyOfficerProps = PrivacyOfficerSectionProps & {
    /** The caller edits their OWN tenant (`!isMaster || ownsViewedTenant`). */
    ownsTenant: boolean;
    isSelfService: boolean;
};

/**
 * The card as the Account page places it — same audience as the legal texts
 * beside it (`shownLegalTexts`): the owner edits, a master viewing reads.
 */
export const AccountPrivacyOfficer = ({ ownsTenant, isSelfService, readOnly, ...rest }: AccountPrivacyOfficerProps) => {
    const { visible, editable } = shownLegalTexts(ownsTenant, isSelfService);
    if (!visible) return null;
    return <PrivacyOfficerSection readOnly={readOnly || !editable} {...rest} />;
};

export default PrivacyOfficerSection;
